import App from 'antd/lib/app';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '../../query-client';
import {
    fetchCustomerUtilityBillFiles,
    fetchCustomerUtilityBillFileById,
    fetchCustomerUtilityBillFilePdfPreviewUrl,
    fetchCustomerUtilityBillFileCustomers,
    fetchCustomerUtilityBillFileSources,
    reparseCustomerUtilityBillFiles,
    ICustomerUtilityBillFilesQuery,
} from './index';
import { IBatchReparseResult } from '../customer-utility-bill';
import { ICustomerUtilityBillFile } from './interface';
import { IPagination } from '../IPagination';

export const useCustomerUtilityBillFilesQuery = (query: ICustomerUtilityBillFilesQuery, options = {}) => {
    return useQuery<IPagination<ICustomerUtilityBillFile>, Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_FILES, query],
        queryFn: ({ signal }) => fetchCustomerUtilityBillFiles(query, signal),
        keepPreviousData: true,
        ...options,
    });
};

export const useCustomerUtilityBillFileSourcesQuery = (options = {}) => {
    return useQuery<string[], Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_FILE_SOURCES],
        queryFn: ({ signal }) => fetchCustomerUtilityBillFileSources(signal),
        ...options,
    });
};

export const useCustomerUtilityBillFileCustomersQuery = (options = {}) => {
    return useQuery<string[], Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_FILE_CUSTOMERS],
        queryFn: ({ signal }) => fetchCustomerUtilityBillFileCustomers(signal),
        ...options,
    });
};

// A file re-parse re-creates/updates its bills too, so the bills list is invalidated alongside the files list.
export const useReparseCustomerUtilityBillFilesMutation = () => {
    const { notification } = App.useApp();
    const queryClient = useQueryClient();

    return useMutation<IBatchReparseResult, Error, string[]>(reparseCustomerUtilityBillFiles, {
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_FILES]);
            queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_REPORTS]);
        },
        onError: (error: Error) => notification.error({ message: error.message || 'Couldn’t re-parse the selected files' }),
    });
};

// Fetches a single file (with source + bills). Used with `refetchInterval` to poll live re-parse status from the
// file modal — the caller passes `enabled`/`refetchInterval` via `options`.
export const useCustomerUtilityBillFileQuery = (id: string, options = {}) => {
    return useQuery<ICustomerUtilityBillFile, Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_FILE_DETAIL, id],
        queryFn: ({ signal }) => fetchCustomerUtilityBillFileById(id, signal),
        ...options,
    });
};

// Pre-signed preview URL for a file's PDF; null when the file has no PDF (404). The URL is short-lived (~15 min),
// so it is allowed to go stale and refetch on reopen.
export const useCustomerUtilityBillFilePdfPreviewQuery = (id: string | null, options = {}) => {
    return useQuery<string | null, Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_FILE_PDF_PREVIEW, id],
        queryFn: ({ signal }) => fetchCustomerUtilityBillFilePdfPreviewUrl(id as string, signal),
        enabled: !!id,
        staleTime: 10 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        ...options,
    });
};

// Downloads a file's PDF on demand with a freshly signed attachment URL (same approach as the bill PDF download).
export const useDownloadCustomerUtilityBillFilePdfMutation = () => {
    const { notification } = App.useApp();

    return useMutation<void, Error, string>(
        async (id: string) => {
            const url = await fetchCustomerUtilityBillFilePdfPreviewUrl(id, null, 'download');
            if (!url) {
                throw new Error('No PDF available for this file');
            }

            const anchor = document.createElement('a');
            anchor.href = url;
            // Ignored cross-origin (S3), but harmless — the signed attachment header is what forces the save.
            anchor.download = `${id}.pdf`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
        },
        {
            onError: (error: Error) => notification.error({ message: error.message || 'Couldn’t download the file PDF' }),
        }
    );
};
